import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Edit } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import { GameProviderWithBannerListTableData } from '@/modules/game/type'
import useMutationCreateProviderLogoFileUrl from '@/modules/game/api/hooks/useMutationCreateProviderLogoFileUrl'
import UploadImage from '@/common/components/UploadImage'
import useMutationUpdateProvider from '@/modules/game/api/hooks/useMutationUpdateProvider'

interface EditProviderLogoProps {
	row: GameProviderWithBannerListTableData
}

export default function EditProviderLogo({ row }: EditProviderLogoProps) {
	const [open, setOpen] = useState(false)
	const [logoUrl, setLogoUrl] = useState(row.logoUrl || '')
	const { mutate: updateProvider } = useMutationUpdateProvider()
	const { mutateAsync: getProviderLogoFileUrl } =
		useMutationCreateProviderLogoFileUrl()
	const queryClient = useQueryClient()

	const handleImageUploaded = (fileUrl: string) => {
		setLogoUrl(fileUrl)
	}

	const handleSave = () => {
		if (!logoUrl) {
			showErrorToast('กรุณาเลือกรูปภาพโลโก้')
			return
		}

		updateProvider(
			{
				id: row.providerId,
				payload: {
					logoUrl: logoUrl,
				},
			},
			{
				onSuccess: () => {
					toast.success('อัพเดทโลโก้สำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameProviderList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="sm">
					<Edit className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>แก้ไขโลโก้ {row.displayName}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<label className="text-sm font-medium">โลโก้ปัจจุบัน</label>
						<div className="flex items-center justify-center rounded border bg-gray-50 p-4">
							{logoUrl ? (
								<img
									src={logoUrl}
									alt={`${row.displayName} logo`}
									className="h-32 w-full rounded object-cover"
								/>
							) : (
								<div className="flex h-32 w-full items-center justify-center text-gray-500">
									ไม่มีโลโก้
								</div>
							)}
						</div>
					</div>
					<div className="space-y-2">
						<label className="text-sm font-medium">
							อัพโหลดโลโก้ใหม่{' '}
							<span className="text-amber-600 dark:text-amber-400">
								(สัดส่วน 1:1)
							</span>
						</label>
						<UploadImage
							onImageUploaded={handleImageUploaded}
							defaultValue={logoUrl}
							getFileUrl={() =>
								getProviderLogoFileUrl({
									payload: { fileExtension: '' },
								})
							}
							aspectRatio="1/1"
							previewHeight={100}
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={() => setOpen(false)}>
							ยกเลิก
						</Button>
						<Button onClick={handleSave}>บันทึก</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
